import { useLeadForm } from "@/features/leads/contexts/FormLeadContext";
import { useCheckLeadPhone } from "@/features/leads/api/useCheckLeadPhone";
import { DEFAULT_COUNTRY_CODE } from "@/constants/countryCodes";

const digitsOf = (v: unknown) => String(v ?? "").replace(/[^\d]/g, "");
const ccOf = (v: unknown) => {
  const raw = String(v ?? "").trim();
  if (!raw) return "";
  return raw.startsWith("+") ? raw : `+${raw}`;
};

/**
 * Duplicate-phone probe for the EDIT drawer.
 *
 * Runs only while the number is actually being CHANGED. That mirrors the
 * server: `LeadUpdate` runs its uniqueness guard behind
 * `resolved.phoneNormalized !== lead.phoneNormalized`, so an edit that leaves
 * the number alone is never rejected for a duplicate — even when the lead does
 * share its number with another row.
 *
 * That distinction is the whole point of this hook. Probing the pre-filled
 * value unconditionally reported a conflict on first render (the debounce seeds
 * from the current value, so it fires with no keystroke), which disabled the
 * drawer's only Save button — including the deal save next to it — and left
 * every phone-colliding lead permanently uneditable. Those collisions are known
 * to exist: the phone-uniqueness migration and the `leads:dedupe` script exist
 * precisely because the index could not be created over them.
 */
export function useEditLeadPhoneCheck() {
  const { watch, lead } = useLeadForm();

  const formPhone = watch("phone");
  const formCc = watch("phoneCountryCode");

  const savedDigits = digitsOf((lead as any)?.phone);
  const savedCc = ccOf((lead as any)?.phoneCountryCode) || DEFAULT_COUNTRY_CODE;
  const currentDigits = digitsOf(formPhone);
  const currentCc = ccOf(formCc) || DEFAULT_COUNTRY_CODE;

  // Treat "no lead loaded yet" as unchanged — never block a save on a probe we
  // have no baseline for.
  const hasBaseline = Boolean(lead) && savedDigits.length > 0;
  const phoneChanged =
    hasBaseline && (currentDigits !== savedDigits || currentCc !== savedCc);

  const check = useCheckLeadPhone({
    phone: formPhone,
    countryCode: formCc,
    excludeLeadId: (lead as any)?.id,
    enabled: phoneChanged,
    // Replace-on-create is CREATE-only: `LeadUpdate` still 409s when the new
    // number collides with ANY lead, trashed/archived included — so the edit
    // probe must keep blocking on those.
    purpose: "edit",
  });

  return {
    ...check,
    /** True only when the user typed a NEW number that is already taken. */
    blocksSave: phoneChanged && check.isDuplicate,
    phoneChanged,
  };
}

export default useEditLeadPhoneCheck;
